/* eslint-disable no-unused-vars */
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { SERVER_BASE_URL } from '../../config';
import { useAuth } from '../../context/authContext';
import { provisionMachinesForCustomerSite } from '../../services/manageSiteMachinesService';
import HeaderTitle from '../HeaderTitle/HeaderTitle';
import ManageSiteMachines from '../ManageSiteMachines/ManageSiteMachines';
import ModalWrapper from "../ModalWrapper/ModalWrapper";

const expectedHeaders = [
  'CustomerName',
  'CustomerNumber',
  'SiteName',
  'SiteNumber',
  'LineName',
  'MachineNumber',
  'MachineName',
  'MachineSerialNo',
];

const ManageMachineProvision = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [submittingProvision, setSubmittingProvision] = useState(false);
  const [provisionRefreshKey, setProvisionRefreshKey] = useState(0);
  const provisionTemplateUrl = `${SERVER_BASE_URL}/templates/ProvisionTemplate.xlsx`;

  const columnsModal = useMemo(() => expectedHeaders.map((header) => ({
    field: header,
    headerName: header,
    width: 180,
  })), []);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      const headers = (jsonData[0] || []).map((header) => String(header || '').trim());
      const isValid = expectedHeaders.every((h) => headers.includes(h));
      if (!isValid) {
        Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.INVALID_FILE_STRUCTURE'), text: t('ALERT_MESSAGES.NOT_REQUIRED_HEADER') });
        return;
      }
      const rows = jsonData
        .slice(1)
        .map((row, rowIndex) =>
          headers.reduce((obj, header, index) => {
            obj[header] = row[index] || '';
            return obj;
          }, { id: rowIndex + 1 })
        )
        .filter((row) =>
          expectedHeaders.some((header) => String(row[header] || '').trim() !== '')
        );
      setTableData(rows);
      setIsModalOpen(true);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleModalSubmit = async () => {
    setSubmittingProvision(true);
    try {
      const responseProvision = await provisionMachinesForCustomerSite(tableData);
      setSubmittingProvision(false);
      setTableData([]);
      setSelectedFile(null);
      setIsModalOpen(false);

      if (!responseProvision.status) {
        Swal.fire({
          icon: 'error',
          title: t('ALERT_MESSAGES.ERROR'),
          text: responseProvision.message || t('ALERT_MESSAGES.PROVISIONING_FAILED'),
        });
        return;
      }

      const results = Array.isArray(responseProvision?.data)
        ? responseProvision.data
        : responseProvision?.data?.results || [];
      const successResults = [], errorResults = [], existsResults = [];
      results.forEach((result) => {
        if (result.status === 'inserted') successResults.push(result);
        else if (result.status === 'error') errorResults.push(result);
        else if (result.status === 'exists') existsResults.push(result);
      });
      if (successResults.length > 0) {
        setProvisionRefreshKey((prev) => prev + 1);
      }
      let successMessage = successResults.length > 0 ? `<b>${successResults.length}</b> machine(s) successfully provisioned` : '';
      let errorMessage = '';
      if (errorResults.length > 0) {
        errorMessage += `<b>${errorResults.length}</b> machine(s) failed to provision due to errors:<br>`;
        errorResults.forEach((r, i) => { errorMessage += `${i + 1}. Machine <b>${r.data?.machine_name || 'Unknown'}</b>: ${r.message}<br>`; });
      }
      let existsMessage = '';
      if (existsResults.length > 0) {
        existsMessage += `<b>${existsResults.length}</b> machine(s) already exist in the system:<br>`;
        existsResults.forEach((r, i) => { existsMessage += `${i + 1}. Machine <b>${r.data?.machine_name || 'Unknown'}</b>: ${r.message}<br>`; });
      }
      Swal.fire({ icon: successResults.length > 0 ? 'success' : 'info', title: t('ALERT_MESSAGES.PROVISIONING_RESULTS'), html: `${successMessage}${errorMessage}${existsMessage}` });
    } catch (error) {
      setSubmittingProvision(false);
      Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: error.message });
    }
  };

  const handleModalCancel = () => {
    setTableData([]);
    setSelectedFile(null);
    setIsModalOpen(false);
  };

  const handleDownloadProvisionTemplate = async (event) => {
    event.preventDefault();
    try {
      const response = await fetch(provisionTemplateUrl, { method: "GET" });
      if (!response.ok) {
        Swal.fire({
          icon: "error",
          title: t("ALERT_MESSAGES.ERROR"),
          text: t('GENERAL.FILE_NOT_AVAILABLE_TO_DOWNLOAD_PLEASE_CONTACT_CCR'),
        });
        return;
      }
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "ProvisionTemplate.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("ALERT_MESSAGES.ERROR"),
        text: t('GENERAL.FILE_DOWNLOAD_FAILED'),
      });
    }
  };

  const actions = (
    <>
      {hasPermission('machine_provision_management.provision_machines') && (
        <>
          <Button variant="contained" component="label" startIcon={<CloudUploadIcon />}>
            {t('BUTTON.UPLOAD_BULK')}
            <input type="file" key={selectedFile || 'file-input'} hidden onChange={handleFileChange} accept=".csv, .xlsx" />
          </Button>
          <Box>
            <Typography variant="body1">{t('UI_TEXT.UPLOAD_BULK_PROVISIONS')}</Typography>
            <Typography variant="body1">
              {t('UI_TEXT.DOWNLOAD')} <Link href={provisionTemplateUrl} onClick={handleDownloadProvisionTemplate} variant="body1" color="primary" underline="hover">{t('UI_TEXT.PROVISION_TEMPLATE')}</Link> {t('UI_TEXT.FOR_REFERENCE')}
            </Typography>
          </Box>
        </>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 0 }}>
      <HeaderTitle title={t('UI_TEXT.MANAGE_MACHINE_PROVISIONS')} subtitle={t('UI_TEXT.ADMIN_CAN_MANAGE_MACHINE_PROVISIONS')} actions={actions} />
      <ManageSiteMachines refreshKey={provisionRefreshKey} />

      <ModalWrapper
        open={isModalOpen}
        setOpen={setIsModalOpen}
        title={`${t('UI_TEXT.FILE_SUMMARY')} ${selectedFile ? ` - ${selectedFile.name}` : ''}`}
        content={
          <>
            <Box m={1} display="flex" gap={1} alignItems="center">
              <AttachFileIcon sx={{ fontSize: 20, color: '#999' }} />
              <Typography variant="body2" color="text.secondary">{t('UI_TEXT.TOTAL_ROWS_UPLOAD',{count: tableData.length})}</Typography>
            </Box>
            <Box sx={{ height: 400, width: '100%', mt: 2 }}>
              <DataGrid rows={tableData} columns={[{ field: 'id', headerName: 'ID', width: 70 }, ...columnsModal]} pageSize={5} rowsPerPageOptions={[5]} checkboxSelection disableRowSelectionOnClick />
            </Box>
          </>
        }
        actions={
          <>
            <Button variant="outlined" color="error" onClick={handleModalCancel} startIcon={<CloseIcon />}>{t('BUTTON.CANCEL')}</Button>
            <Button variant="contained" color="primary" onClick={handleModalSubmit} disabled={submittingProvision}>
              {submittingProvision ? (<><CircularProgress size={16} sx={{ color: 'white', mr: 1 }} /> {t('BUTTON.SAVING')}</>) : t('BUTTON.SUBMIT_PROVISION')}
            </Button>
          </>
        }
      />
    </Box>
  );
};

export default ManageMachineProvision;
